var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _imageRatioSetting, _ASPECT_RATIOS, _PaginatedListAspectRatioHelper_instances, storeImageRatioSettings_fn, fixAdaptiveAspectRatios_fn, applyFixedAspectRatio_fn, getSafeImageAspectRatio_fn, getAspectRatioValue_fn, applyAspectRatioToGallery_fn, getUnprocessedGalleries_fn, markAsProcessed_fn;
class PaginatedListAspectRatioHelper {
  /**
   * @param {Object} options - The options object
   * @param {HTMLElement} options.templateCard - The template card gallery element to get the image ratio from
   */
  constructor({ templateCard }) {
    __privateAdd(this, _PaginatedListAspectRatioHelper_instances);
    /** @type {string | null} */
    __privateAdd(this, _imageRatioSetting, null);
    /**
     * Aspect ratio values matching the theme's standardized values
     * @type {Object.<string, string>}
     */
    __privateAdd(this, _ASPECT_RATIOS, {
      square: "1",
      portrait: "0.8",
      landscape: "1.778"
    });
    if (!Shopify.designMode) return;
    __privateMethod(this, _PaginatedListAspectRatioHelper_instances, storeImageRatioSettings_fn).call(this, templateCard);
  }
  /**
   * Process newly added elements and apply correct aspect ratios
   */
  processNewElements() {
    if (!Shopify.designMode) return;
    requestAnimationFrame(() => {
      __privateGet(this, _imageRatioSetting) === "adapt" ? __privateMethod(this, _PaginatedListAspectRatioHelper_instances, fixAdaptiveAspectRatios_fn).call(this) : __privateMethod(this, _PaginatedListAspectRatioHelper_instances, applyFixedAspectRatio_fn).call(this);
    });
  }
}
_imageRatioSetting = new WeakMap();
_ASPECT_RATIOS = new WeakMap();
_PaginatedListAspectRatioHelper_instances = new WeakSet();
/**
 * Store the image ratio from the template card for later use
 * @param {HTMLElement} templateCard - The template card gallery element to get the image ratio from
 */
storeImageRatioSettings_fn = function(templateCard) {
  __privateSet(this, _imageRatioSetting, templateCard.getAttribute("data-image-ratio"));
};
/**
 * Fix adaptive aspect ratios for newly added cards
 * For the 'adapt' setting, each product should use its own image's aspect ratio
 */
fixAdaptiveAspectRatios_fn = function() {
  const newCardGalleries = __privateMethod(this, _PaginatedListAspectRatioHelper_instances, getUnprocessedGalleries_fn).call(this);
  if (!newCardGalleries.length) return;
  const productRatioCache = /* @__PURE__ */ new Map();
  newCardGalleries.forEach((gallery) => {
    if (!(gallery instanceof HTMLElement)) return;
    const productId = gallery.getAttribute("data-product-id");
    if (productId && productRatioCache.has(productId)) {
      __privateMethod(this, _PaginatedListAspectRatioHelper_instances, applyAspectRatioToGallery_fn).call(this, gallery, productRatioCache.get(productId));
      return;
    }
    const img = gallery.querySelector("img");
    if (!img) {
      __privateMethod(this, _PaginatedListAspectRatioHelper_instances, applyAspectRatioToGallery_fn).call(this, gallery, "1");
      return;
    }
    const loadAndSetRatio = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      const imgRatio = __privateMethod(this, _PaginatedListAspectRatioHelper_instances, getSafeImageAspectRatio_fn).call(this, img.naturalWidth, img.naturalHeight);
      if (productId) {
        productRatioCache.set(productId, imgRatio);
      }
      __privateMethod(this, _PaginatedListAspectRatioHelper_instances, applyAspectRatioToGallery_fn).call(this, gallery, imgRatio);
    };
    if (img.complete) {
      loadAndSetRatio();
    } else {
      img.addEventListener("load", loadAndSetRatio, { once: true });
    }
  });
};
/**
 * Apply a fixed aspect ratio to all card-gallery and media container elements
 * Only used for non-adaptive modes (square, portrait, landscape)
 */
applyFixedAspectRatio_fn = function() {
  if (!__privateGet(this, _imageRatioSetting)) return;
  const aspectRatio = __privateMethod(this, _PaginatedListAspectRatioHelper_instances, getAspectRatioValue_fn).call(this, __privateGet(this, _imageRatioSetting));
  if (!aspectRatio) return;
  const newCardGalleries = __privateMethod(this, _PaginatedListAspectRatioHelper_instances, getUnprocessedGalleries_fn).call(this);
  if (!newCardGalleries.length) return;
  requestAnimationFrame(() => {
    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;
      __privateMethod(this, _PaginatedListAspectRatioHelper_instances, applyAspectRatioToGallery_fn).call(this, gallery, aspectRatio);
    });
  });
};
/**
 * Calculate a safe aspect ratio value from image dimensions
 * Ensures the ratio stays within reasonable bounds and has consistent decimal places
 * @param {number} width - Natural width of the image
 * @param {number} height - Natural height of the image
 * @returns {string} Normalized aspect ratio as a string
 */
getSafeImageAspectRatio_fn = function(width, height) {
  const rawRatio = width / height;
  return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
};
/**
 * Get aspect ratio value based on setting
 * @param {string} ratioSetting - The ratio setting name
 * @returns {string|null} - The aspect ratio value or null
 */
getAspectRatioValue_fn = function(ratioSetting) {
  return __privateGet(this, _ASPECT_RATIOS)[ratioSetting] || null;
};
/**
 * Apply an aspect ratio to a gallery and all its media containers
 * @param {HTMLElement} gallery - The gallery element
 * @param {string} aspectRatio - The aspect ratio to apply
 */
applyAspectRatioToGallery_fn = function(gallery, aspectRatio) {
  if (!(gallery instanceof HTMLElement)) return;
  gallery.style.setProperty("--gallery-aspect-ratio", aspectRatio);
  const mediaContainers = gallery.querySelectorAll(".product-media-container");
  mediaContainers.forEach((container) => {
    if (container instanceof HTMLElement) {
      container.style.aspectRatio = aspectRatio;
    }
  });
  __privateMethod(this, _PaginatedListAspectRatioHelper_instances, markAsProcessed_fn).call(this, gallery);
};
/**
 * Get all unprocessed card galleries
 * @returns {NodeListOf<Element>} List of unprocessed galleries
 */
getUnprocessedGalleries_fn = function() {
  return document.querySelectorAll(".card-gallery:not([data-aspect-ratio-applied])");
};
/**
 * Mark gallery as processed
 * @param {HTMLElement} gallery - The gallery element to mark as processed
 */
markAsProcessed_fn = function(gallery) {
  if (!(gallery instanceof HTMLElement)) return;
  gallery.setAttribute("data-aspect-ratio-applied", "true");
};
export {
  PaginatedListAspectRatioHelper
};
